// src/pages/Listings.js

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    Snackbar,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {
    getListings,
    getSubcategories,
    getWishlist,
    getTwoLevelCategories,
    searchListings,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import SEOProvider from '../components/SEOProvider';

import SearchBar from '../components/SearchBar';
import ListingsGrid from '../components/ListingsGrid';
import FiltersDialog from '../components/FiltersDialog';
import ListingsMapView from '../components/ListingsMapView';
import ListingsHeader from '../components/ListingsHeader';
import ViewToggle from '../components/ViewToggle'
import { StyledButton } from '../components/StyledComponents';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    maxWidth: '1400px',
}));

function parseLocalDateString(dateStr) {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    // Note that JS months are 0-based, so month - 1
    return new Date(year, month - 1, day);
}

function formatDateToLocalYYYYMMDD(date) {
    if (!(date instanceof Date)) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}


// Helper functions for URL params
const serializeSearchParams = (params) => {
    const newParams = {};

    if (params.searchQuery) {
        newParams.q = params.searchQuery;
    }
    if (params.selectedCategory?.id) {
        newParams.category = params.selectedCategory.id;
    }
    if (params.dateRange?.[0] instanceof Date) {
        newParams.startDate = formatDateToLocalYYYYMMDD(params.dateRange[0]);
    }
    if (params.dateRange?.[1] instanceof Date) {
        newParams.endDate = formatDateToLocalYYYYMMDD(params.dateRange[1]);
    }

    return newParams;
};

const deserializeSearchParams = (params) => {
    const startDateStr = params.get('startDate'); // e.g. "2025-01-19"
    const endDateStr = params.get('endDate');   // e.g. "2025-01-24"
    const categoryStr = params.get('category'); // e.g. "1"
    const categoryId = categoryStr ? parseInt(categoryStr, 10) : null;

    return {
        searchQuery: params.get('q') || '',
        selectedCategory: categoryId ? { id: categoryId } : null,
        // Convert those strings to local Date objects:
        dateRange: [
            parseLocalDateString(startDateStr),
            parseLocalDateString(endDateStr),
        ],
    };
};

const Listings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    // Initialize state from URL params
    const initialParams = deserializeSearchParams(searchParams);

    const [searchInput, setSearchInput] = useState(initialParams.searchQuery);
    const [searchQuery, setSearchQuery] = useState(initialParams.searchQuery);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(initialParams.selectedCategory);
    const [subcategories, setSubcategories] = useState([]);
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [sortOrder, setSortOrder] = useState('relevance');
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [dateRange, setDateRange] = useState(
        initialParams.dateRange[0] && initialParams.dateRange[1]
            ? [new Date(initialParams.dateRange[0]), new Date(initialParams.dateRange[1])]
            : [null, null]
    );
    const [viewMode, setViewMode] = useState('grid');
    const [wishlistedItems, setWishlistedItems] = useState(new Set());
    const { currentUser } = useAuth();

    const ITEMS_PER_PAGE = 12;
    const SCROLL_DEBOUNCE_DELAY = 300;
    const LOAD_MORE_DELAY = 500;

    // Environment variable to toggle loading method
    const USE_INFINITE_SCROLL =
        process.env.REACT_APP_USE_INFINITE_SCROLL === 'true';

    // Replace your use of "listings" in the view with "filteredListings":
    const filteredListings = listings
        .filter(l => l.price >= priceRange[0] && l.price <= priceRange[1])
        .filter(l =>
            selectedSubcategories.length > 0
                ? selectedSubcategories.includes(l.subcategory)
                : true
        )
        .slice()   // make a shallow copy
        .sort((a, b) => {
            if (sortOrder === 'asc') return a.price - b.price;
            if (sortOrder === 'desc') return b.price - a.price;
            return 0;  // relevance: keep original order
        });
    // -----------------------------
    // Wishlist
    // -----------------------------
    const fetchWishlist = useCallback(async () => {
        if (!currentUser) return;
        try {
            const response = await getWishlist();
            const wishlistedIds = new Set(response.data.map((item) => item.listing_id));
            setWishlistedItems(wishlistedIds);
        } catch (error) {
            console.error('Error fetching wishlist:', error);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchWishlist();
    }, [fetchWishlist]);

    const handleWishlistUpdate = useCallback((listingId, isWishlisted) => {
        setWishlistedItems((prev) => {
            const newSet = new Set(prev);
            if (isWishlisted) {
                newSet.add(listingId);
            } else {
                newSet.delete(listingId);
            }
            return newSet;
        });
    }, []);

    // -----------------------------
    // Categories / Subcategories
    // -----------------------------
    const fetchTopLevelCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const response = await getTwoLevelCategories();
            if (response?.data) {
                setCategories(response.data);
            } else {
                throw new Error('Invalid response format from categories API');
            }
        } catch (error) {
            console.error('Error fetching categories', error);
            setError('Failed to fetch categories. Please try again later.');
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTopLevelCategories();
    }, [fetchTopLevelCategories]);

    const fetchSubcategoriesCallback = useCallback(async (categoryId) => {
        try {
            setCategoriesLoading(true);
            const response = await getSubcategories(categoryId);
            setSubcategories(response.data);
        } catch (error) {
            console.error('Error fetching subcategories', error);
            setError('Failed to fetch subcategories. Please try again later.');
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    // -----------------------------
    // Listings (initial load)
    // -----------------------------
    const fetchInitialListings = useCallback(async () => {
        setLoading(true);
        setPage(0);
        setHasMore(true);

        try {
            let response;
            const formattedStart = formatDateToLocalYYYYMMDD(dateRange?.[0]);
            const formattedEnd = formatDateToLocalYYYYMMDD(dateRange?.[1]);
            if (searchQuery.trim() !== '') {
                response = await searchListings(
                    searchQuery,
                    ITEMS_PER_PAGE,
                    0,
                    selectedCategory?.id,
                    formattedStart,
                    formattedEnd
                );
            } else {
                response = await getListings(
                    ITEMS_PER_PAGE,
                    0,
                    selectedCategory?.id,
                    formattedStart,
                    formattedEnd
                );
            }

            if (!response) {
                throw new Error('No response from listings API');
            }

            let fetchedListings = [];

            if (Array.isArray(response)) {
                fetchedListings = response;
            } else if (response?.data && Array.isArray(response.data)) {
                fetchedListings = response.data;
            } else if (
                response?.data?.listings &&
                Array.isArray(response.data.listings)
            ) {
                fetchedListings = response.data.listings;
            } else {
                throw new Error('Unexpected API response structure');
            }

            if (!Array.isArray(fetchedListings)) {
                throw new Error('Invalid listings data format');
            }

            setListings(fetchedListings);
            setHasMore(fetchedListings.length === ITEMS_PER_PAGE);
        } catch (error) {
            console.error('Error fetching listings:', error);
            setError('Failed to fetch listings. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [
        searchQuery,
        selectedCategory,
        ITEMS_PER_PAGE,
        dateRange
    ]);

    useEffect(() => {
        fetchInitialListings();
    }, [fetchInitialListings]);

    // -----------------------------
    // Fetch more (paging)
    // -----------------------------
    const fetchMoreListings = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);

        setTimeout(async () => {
            try {
                const nextPage = page + 1;
                let response;
                const formattedStart = formatDateToLocalYYYYMMDD(dateRange?.[0]);
                const formattedEnd = formatDateToLocalYYYYMMDD(dateRange?.[1]);
                if (searchQuery.trim() !== '') {
                    response = await searchListings(
                        searchQuery,
                        ITEMS_PER_PAGE,
                        nextPage * ITEMS_PER_PAGE,
                        selectedCategory?.id,
                        formattedStart,
                        formattedEnd
                    );
                } else {
                    response = await getListings(
                        ITEMS_PER_PAGE,
                        nextPage * ITEMS_PER_PAGE,
                        selectedCategory?.id,
                        formattedStart,
                        formattedEnd
                    );
                }

                let fetchedListings = [];

                if (Array.isArray(response)) {
                    fetchedListings = response;
                } else if (response.data && Array.isArray(response.data)) {
                    fetchedListings = response.data;
                } else if (
                    response.data &&
                    response.data.listings &&
                    Array.isArray(response.data.listings)
                ) {
                    fetchedListings = response.data.listings;
                } else {
                    console.warn('Unexpected API response structure:', response);
                }

                setListings((prevListings) => [...prevListings, ...fetchedListings]);
                setHasMore(fetchedListings.length === ITEMS_PER_PAGE);
                setPage(nextPage);
            } catch (error) {
                console.error('Error fetching more listings:', error);
                setError('Failed to load more listings. Please try again later.');
            } finally {
                setLoading(false);
            }
        }, LOAD_MORE_DELAY);
    }, [
        loading,
        hasMore,
        page,
        searchQuery,
        selectedCategory,
        ITEMS_PER_PAGE,
        LOAD_MORE_DELAY,
        dateRange
    ]);

    // -----------------------------
    // Infinite Scroll
    // -----------------------------
    const handleScroll = useCallback(() => {
        let timeoutId = null;

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                if (loading || !hasMore) return;
                const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
                if (scrollTop + clientHeight >= scrollHeight - 100) {
                    fetchMoreListings();
                }
            }, SCROLL_DEBOUNCE_DELAY);
        };
    }, [loading, hasMore, fetchMoreListings, SCROLL_DEBOUNCE_DELAY]);

    useEffect(() => {
        if (USE_INFINITE_SCROLL) {
            const scrollHandler = handleScroll();
            window.addEventListener('scroll', scrollHandler);
            return () => window.removeEventListener('scroll', scrollHandler);
        }
    }, [USE_INFINITE_SCROLL, handleScroll]);

    // -----------------------------
    // "Load More" Button
    // -----------------------------
    const handleLoadMoreClick = useCallback(() => {
        fetchMoreListings();
    }, [fetchMoreListings]);

    // -----------------------------
    // Search & Filters
    // -----------------------------
    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearchSubmit = (selectedDateRange, forcedCategory) => {
        // If forcedCategory is undefined, we default to the existing selectedCategory
        const finalCategory = (typeof forcedCategory !== 'undefined')
            ? forcedCategory
            : selectedCategory;

        const newSearchQuery = searchInput;
        const newDateRange = Array.isArray(selectedDateRange)
            ? selectedDateRange
            : [null, null];

        // Update state to reflect the new category
        setSelectedCategory(finalCategory);

        // Update the URL
        setSearchParams(serializeSearchParams({
            searchQuery: newSearchQuery,
            selectedCategory: finalCategory,
            dateRange: newDateRange
        }));

        // Also update local state
        setSearchQuery(newSearchQuery);
        setDateRange(newDateRange);
        setPage(0);
        setHasMore(true);
    };
    const handleClearFilters = () => {
        // Clear URL params
        setSearchParams({});

        setSearchInput('');
        setSearchQuery('');
        setSelectedCategory(null);
        setSelectedSubcategories([]);
        setPriceRange([0, 1000]);
        setPage(0);
        setHasMore(true);
        setSubcategories([]);
    };

    const handleCategorySelect = async (category) => {
        // Toggle logic
        const newCategory = (selectedCategory?.id === category.id) ? null : category;

        // If we have a newCategory (not null), fetch subcategories.
        // (You can do this after handleSearchSubmit if you want.)
        if (newCategory) {
            await fetchSubcategoriesCallback(newCategory.id);
            setSelectedSubcategories([]);
        } else {
            setSubcategories([]);
        }

        // Now do ONE call that sets the URL + triggers the search
        // Pass the newly toggled category so that the final URL is consistent
        handleSearchSubmit(dateRange, newCategory);
    };

    const handleFilterOpen = () => {
        setFilterDialogOpen(true);
    };

    const handleFilterClose = () => {
        setFilterDialogOpen(false);
    };

    const handleApplyFilters = () => {
        setFilterDialogOpen(false);
    };

    const handleClearAllFilters = () => {
        handleClearFilters();
        setFilterDialogOpen(false);
    };

    const handlePriceChange = (event, newValue) => {
        setPriceRange(newValue);
    };

    const handleSubcategoryChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedSubcategories(typeof value === 'string' ? value.split(',') : value);
    };

    // -----------------------------
    // Render
    // -----------------------------
    return (
        <SEOProvider
            title="Browse Items to Borrow"
            description="Browse and borrow items from your local community. Find everything from tools to sports equipment, cameras to camping gear."
            type="website"
        >
            <StyledContainer maxWidth="xl">
                <Box mb={3}>
                    <SearchBar
                        searchInput={searchInput}
                        onSearchInputChange={handleSearchInputChange}
                        onSearchSubmit={handleSearchSubmit}
                        onFilterClick={handleFilterOpen}  // Add this line
                        initialDateRange={dateRange}
                    />
                </Box>
                <ListingsHeader
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onCategorySelect={handleCategorySelect}
                    loading={categoriesLoading}
                />

                <ViewToggle
                    view={viewMode}
                    onViewChange={(newView) => {
                        if (newView !== null) {
                            setViewMode(newView);
                        }
                    }}
                />
                {viewMode === 'grid' ? (
                    <ListingsGrid
                        listings={filteredListings}
                        wishlistedItems={wishlistedItems}
                        onWishlistUpdate={handleWishlistUpdate}
                        dateRange={dateRange}
                    />
                ) : (
                    <ListingsMapView
                        listings={listings}
                        wishlistedItems={wishlistedItems}
                        onWishlistUpdate={handleWishlistUpdate}
                    />
                )}

                {loading && (
                    <Box mt={3} display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                )}

                {!loading && hasMore && (
                    <Box mt={3} display="flex" justifyContent="center">
                        {USE_INFINITE_SCROLL ? (
                            <Typography variant="body2">
                                Scroll down to load more listings...
                            </Typography>
                        ) : (
                            <StyledButton
                                variant="contained"
                                color="primary"
                                onClick={handleLoadMoreClick}
                                startIcon={<AddIcon />}
                            >
                                Load More
                            </StyledButton>
                        )}
                    </Box>
                )}

                <FiltersDialog
                    open={filterDialogOpen}
                    onClose={handleFilterClose}
                    priceRange={priceRange}
                    onPriceChange={handlePriceChange}
                    subcategories={subcategories}
                    selectedSubcategories={selectedSubcategories}
                    onSubcategoryChange={handleSubcategoryChange}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearAllFilters}
                    sortOrder={sortOrder}
                    onSortOrderChange={(e) => setSortOrder(e.target.value)}
                    priceData={filteredListings.map((listing) => listing.price)}
                    originalPriceData={listings.map(listing => listing.price)}
                />

                <Snackbar
                    open={Boolean(error)}
                    autoHideDuration={6000}
                    onClose={() => setError('')}
                    message={error}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={() => setError('')}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                />
            </StyledContainer>
        </SEOProvider>
    );
};

export default Listings;
